import type { ProjectStructureView } from '@obraplan/core/application/use-cases/structure/get-project-structure';
import type { ProjectTakeoff } from '@obraplan/core/application/use-cases/takeoff/takeoff-use-cases';
import { resolveItemQuantity } from '@obraplan/core/domain/takeoff/consolidation';
import type { Service } from '@obraplan/core/domain/takeoff/service';
import {
  serviceKey,
  type CatalogDiscipline,
  type CatalogService,
  type ServiceCatalog,
} from '@obraplan/core/domain/takeoff/service-catalog';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import type { DisciplineId, ServiceId, UnitTypeId } from '@obraplan/core/domain/shared/ids';
import { pecasDosServicos } from './pecas-importadas';
import { quantitiesOf } from './resumo-dos-itens';
import type { TypologyDraft } from './typology-draft';

/**
 * O que cada tipologia mostra — e por quê.
 *
 * O catálogo da obra traz os serviços dela e os ~150 globais (RN-007), e todas as telas do
 * quantitativo os mostravam inteiros em toda tipologia. Três cortes, nesta ordem, decidem o
 * que sobra:
 *
 * 1. **A disciplina está nos andares da tipologia.** Quem diz é a estrutura: o pavimento
 *    tem a atividade daquela disciplina. A tipologia acontece nos andares ligados aos
 *    desenhos onde ela tem posição.
 * 2. **O serviço foi escolhido para a tipologia** — gravado no servidor, ou, enquanto
 *    ninguém decidiu, o ponto de partida: os serviços que já têm quantidade nela.
 * 3. **O filtro do topo**, quando há: uma disciplina, e dentro dela um serviço.
 *
 * O resultado é um `ServiceCatalog` com a mesma forma do original, e é isso que as telas
 * recebem. A lista, os aptos, a consolidação e o orçamento continuam lendo um catálogo —
 * só que o **da tipologia** —, e por isso concordam entre si sem cada um refazer o corte.
 */

/** O que a tela conhece da obra para recortar qualquer tipologia. */
export interface EscopoDaObra {
  /** As disciplinas (chave normalizada) dos andares onde cada tipologia acontece. */
  readonly disciplinasPorTipologia: ReadonlyMap<UnitTypeId, ReadonlySet<string>>;
  /** O nome de cada disciplina da estrutura, pela chave — para listar a que não tem serviço. */
  readonly nomeDaDisciplina: ReadonlyMap<string, string>;
  /** A posição da disciplina no cadastro — a ordem de execução da obra, e não a alfabética. */
  readonly ordemDaDisciplina: ReadonlyMap<string, number>;
  /** O serviço gravado de cada linha do catálogo — é o id que a escolha grava. */
  readonly servicoPorChave: ReadonlyMap<string, Service>;
  /** A escolha gravada, já em chaves do catálogo. Ausente = ninguém decidiu ainda. */
  readonly escolhaGravada: ReadonlyMap<UnitTypeId, ReadonlySet<string>>;
}

export function montarEscopoDaObra(
  estrutura: ProjectStructureView,
  quantitativo: ProjectTakeoff,
  servicos: readonly Service[],
): EscopoDaObra {
  const nomePorId = new Map<DisciplineId, string>(
    estrutura.availableDisciplines.map((disciplina) => [disciplina.id, disciplina.name]),
  );
  const ordemDaDisciplina = new Map<string, number>(
    estrutura.availableDisciplines.map((disciplina) => [
      normalizeName(disciplina.name),
      disciplina.position,
    ]),
  );

  const servicoPorChave = new Map<string, Service>();
  const chavePorId = new Map<ServiceId, string>();
  for (const servico of servicos) {
    const disciplina = nomePorId.get(servico.disciplineId);
    if (disciplina === undefined) continue;
    const chave = serviceKey(disciplina, servico.name);
    servicoPorChave.set(chave, servico);
    chavePorId.set(servico.id, chave);
  }

  // Andar → disciplinas, pela atividade da estrutura.
  const nomeDaDisciplina = new Map<string, string>();
  const disciplinasPorDesenho = new Map<string, Set<string>>();
  for (const torre of estrutura.tree.towers) {
    for (const andar of torre.floors) {
      const desenho = andar.floor.floorPlanId;
      if (desenho === undefined) continue;
      const doDesenho = disciplinasPorDesenho.get(desenho) ?? new Set<string>();
      for (const { discipline } of andar.activities) {
        const chave = normalizeName(discipline.name);
        doDesenho.add(chave);
        nomeDaDisciplina.set(chave, discipline.name);
      }
      disciplinasPorDesenho.set(desenho, doDesenho);
    }
  }

  // Tipologia → desenhos onde tem posição → disciplinas desses andares.
  const disciplinasPorTipologia = new Map<UnitTypeId, Set<string>>();
  for (const posicao of quantitativo.positions) {
    const daTipologia = disciplinasPorTipologia.get(posicao.unitTypeId) ?? new Set<string>();
    for (const chave of disciplinasPorDesenho.get(posicao.floorPlanId) ?? [])
      daTipologia.add(chave);
    disciplinasPorTipologia.set(posicao.unitTypeId, daTipologia);
  }

  const escolhaGravada = new Map<UnitTypeId, ReadonlySet<string>>();
  for (const escolha of quantitativo.serviceChoices) {
    escolhaGravada.set(
      escolha.unitTypeId,
      new Set(
        escolha.serviceIds
          .map((id) => chavePorId.get(id))
          .filter((chave): chave is string => chave !== undefined),
      ),
    );
  }

  return {
    disciplinasPorTipologia,
    nomeDaDisciplina,
    ordemDaDisciplina,
    servicoPorChave,
    escolhaGravada,
  };
}

/** O serviço já tem quantidade nesta tipologia — digitada ou vinda da aba da planilha? */
export function temQuantidade(servico: CatalogService, typology: TypologyDraft): boolean {
  // O número que a tipologia deu ao próprio serviço também é quantidade (Fase 24, fatia 5).
  if (typology.serviceQuantities?.[servico.key] !== undefined) return true;
  // A peça importada também (Fase 28): o número do serviço nela, ou o de um item — este pelo
  // `fromPieces` que `quantitiesOf` já traz.
  if (pecasDosServicos(typology.pieceQuantities)[servico.key] !== undefined) return true;
  const quantities = quantitiesOf(typology);
  if (
    servico.items.some(
      (item) => resolveItemQuantity(item, servico.key, quantities).quantity !== undefined,
    )
  ) {
    return true;
  }
  const daAba =
    typology.sheetName === undefined ? undefined : servico.quantityBySheet[typology.sheetName];
  return daAba !== undefined && daAba > 0;
}

/**
 * O ponto de partida de quem nunca escolheu: os serviços que já têm quantidade.
 *
 * Não é gravado sozinho. As quantidades ainda moram no navegador, e a primeira máquina a
 * abrir a tela — talvez uma sem quantidade nenhuma — gravaria uma escolha vazia por cima
 * do trabalho feito na outra. Fica proposto até alguém salvar a escolha no painel.
 */
export function escolhaInicial(
  catalog: ServiceCatalog,
  typology: TypologyDraft,
): ReadonlySet<string> {
  const chaves = new Set<string>();
  for (const disciplina of catalog.disciplines) {
    for (const servico of disciplina.services) {
      if (temQuantidade(servico, typology)) chaves.add(servico.key);
    }
  }
  return chaves;
}

export interface EscolhaDaTipologia {
  readonly chaves: ReadonlySet<string>;
  /** Gravada no servidor, ou só proposta a partir das quantidades. */
  readonly gravada: boolean;
}

export function escolhaDaTipologia(
  escopo: EscopoDaObra,
  catalog: ServiceCatalog,
  typology: TypologyDraft,
): EscolhaDaTipologia {
  const gravada = escopo.escolhaGravada.get(typology.unitTypeId);
  return gravada === undefined
    ? { chaves: escolhaInicial(catalog, typology), gravada: false }
    : { chaves: gravada, gravada: true };
}

export interface FiltroDoQuantitativo {
  /** Chave normalizada da disciplina. */
  readonly disciplina?: string;
  /** Chave do serviço no catálogo (`disciplina|serviço`). */
  readonly servico?: string;
}

export interface RecorteDaTipologia {
  /** Os escolhidos das disciplinas do andar, mais o filtro — é o que as telas mostram. */
  readonly catalogo: ServiceCatalog;
  /**
   * Escolhidos **com quantidade** cuja disciplina não está nos andares da tipologia.
   *
   * Entram no catálogo recortado — somem só se alguém desmarcar —, e a lista os marca com
   * o aviso. Esconder o que já foi lançado porque a estrutura mudou seria perder trabalho
   * calado.
   */
  readonly foraDoAndar: ReadonlySet<string>;
  readonly disciplinasDoAndar: ReadonlySet<string>;
  readonly escolha: EscolhaDaTipologia;
}

export function recortarTipologia(
  catalog: ServiceCatalog,
  typology: TypologyDraft,
  escopo: EscopoDaObra,
  filtro: FiltroDoQuantitativo = {},
): RecorteDaTipologia {
  const disciplinasDoAndar = escopo.disciplinasPorTipologia.get(typology.unitTypeId) ?? new Set();
  const escolha = escolhaDaTipologia(escopo, catalog, typology);
  const foraDoAndar = new Set<string>();

  const disciplines = catalog.disciplines
    .filter((disciplina) => filtro.disciplina === undefined || disciplina.key === filtro.disciplina)
    .map((disciplina): CatalogDiscipline => {
      const noAndar = disciplinasDoAndar.has(disciplina.key);
      const services = disciplina.services.filter((servico) => {
        if (!escolha.chaves.has(servico.key)) return false;
        if (filtro.servico !== undefined && servico.key !== filtro.servico) return false;
        if (noAndar) return true;
        if (!temQuantidade(servico, typology)) return false;
        foraDoAndar.add(servico.key);
        return true;
      });
      return { ...disciplina, services };
    })
    .filter((disciplina) => disciplina.services.length > 0)
    // A ordem de execução da obra, como no painel: pilar antes de alvenaria. O catálogo
    // guarda a ordem em que as disciplinas chegaram, que não quer dizer nada para quem lança.
    .map((disciplina, indice) => ({ disciplina, indice }))
    .sort(
      (a, b) =>
        (escopo.ordemDaDisciplina.get(a.disciplina.key) ?? Number.MAX_SAFE_INTEGER) -
          (escopo.ordemDaDisciplina.get(b.disciplina.key) ?? Number.MAX_SAFE_INTEGER) ||
        a.indice - b.indice,
    )
    .map(({ disciplina }) => disciplina);

  return {
    catalogo: { ...catalog, disciplines },
    foraDoAndar,
    disciplinasDoAndar,
    escolha,
  };
}

// ── O que o painel de escolha oferece ──────────────────────────────────────────

export interface OfertaDoServico {
  readonly servico: CatalogService;
  /** O id no servidor. Ausente = o serviço só existe neste navegador, e ainda não subiu. */
  readonly id?: ServiceId;
  readonly desativado: boolean;
  readonly temQuantidade: boolean;
}

export interface OfertaDaDisciplina {
  readonly key: string;
  readonly name: string;
  readonly servicos: readonly OfertaDoServico[];
}

/**
 * As disciplinas dos andares, com os serviços de cada uma — o que o painel oferece.
 *
 * A disciplina do andar sem serviço nenhum aparece mesmo assim: é ali que se cria o
 * primeiro. O serviço global desativado não é oferecido, a menos que já esteja escolhido —
 * aí aparece marcado como desativado, para poder ser tirado.
 */
export function ofertaDoPainel(
  catalog: ServiceCatalog,
  typology: TypologyDraft,
  escopo: EscopoDaObra,
  escolhidos: ReadonlySet<string>,
): readonly OfertaDaDisciplina[] {
  const doAndar = escopo.disciplinasPorTipologia.get(typology.unitTypeId) ?? new Set<string>();
  const porChave = new Map(catalog.disciplines.map((disciplina) => [disciplina.key, disciplina]));

  return [...doAndar]
    .map((chave): OfertaDaDisciplina => {
      const disciplina = porChave.get(chave);
      const servicos = (disciplina?.services ?? [])
        .map((servico): OfertaDoServico => {
          const gravado = escopo.servicoPorChave.get(servico.key);
          return {
            servico,
            ...(gravado === undefined ? {} : { id: gravado.id }),
            desativado: gravado !== undefined && !gravado.isActive,
            temQuantidade: temQuantidade(servico, typology),
          };
        })
        .filter((oferta) => !oferta.desativado || escolhidos.has(oferta.servico.key));
      return {
        key: chave,
        name: disciplina?.name ?? escopo.nomeDaDisciplina.get(chave) ?? chave,
        servicos,
      };
    })
    .sort(
      (a, b) =>
        (escopo.ordemDaDisciplina.get(a.key) ?? Number.MAX_SAFE_INTEGER) -
          (escopo.ordemDaDisciplina.get(b.key) ?? Number.MAX_SAFE_INTEGER) ||
        a.name.localeCompare(b.name, 'pt-BR'),
    );
}

// ── A aba da planilha ligada à tipologia ───────────────────────────────────────

export interface ServicoDaAba {
  readonly disciplina: string;
  readonly servico: CatalogService;
}

/**
 * O que a aba da planilha quantifica e a tipologia ainda não escolheu.
 *
 * É o que a tela pergunta ao ligar a aba: "a aba Apto Tipo 1 orça dezoito serviços;
 * marcar?". Só entram os das disciplinas dos andares — a regra do painel vale aqui também,
 * e a aba que orça pintura de fachada não a enfia num apartamento sem pintura.
 */
export function servicosDaAba(
  catalog: ServiceCatalog,
  sheetName: string,
  disciplinasDoAndar: ReadonlySet<string>,
  escolhidos: ReadonlySet<string>,
): readonly ServicoDaAba[] {
  const orcados: ServicoDaAba[] = [];
  for (const disciplina of catalog.disciplines) {
    if (!disciplinasDoAndar.has(disciplina.key)) continue;
    for (const servico of disciplina.services) {
      if (escolhidos.has(servico.key)) continue;
      const doServico = servico.quantityBySheet[sheetName];
      const orcado =
        (doServico !== undefined && doServico > 0) ||
        servico.items.some((item) => (item.quantityBySheet[sheetName] ?? 0) > 0);
      if (orcado) orcados.push({ disciplina: disciplina.name, servico });
    }
  }
  return orcados;
}
